import {
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
} from '@mui/material';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import {
  bindHover,
  bindPopover,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { DriveContentType } from 'src/@types/drive';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useConfirmationModal } from 'src/components/ConfirmationModal';
import Iconify from 'src/components/iconify/Iconify';
import { useSnackbar } from 'src/components/snackbar';
import useCopyToClipboard from 'src/hooks/useCopyToClipboard';
import { useLocales } from 'src/locales';
import { getPublicUrl } from 'src/sections/@dashboard/drive';
import { useDriveContentEditDialog } from 'src/sections/@dashboard/drive/portal/DriveContentEditDialog';
import { useShareDriveContentDialog } from 'src/sections/@dashboard/drive/share-drive-content';
import { useDriveContentPublicTokenQuery } from 'src/sections/@dashboard/drive/shared/useDriveContentPublicTokenQuery';
import { useDeleteContent } from 'src/sections/@dashboard/drive/useDeleteContent';
import type { ContentActionsProps } from './types';

export const CloudContentActions = ({
  driveContent,
  downloadContent,
}: ContentActionsProps) => {
  const { user: currentUser } = useAuthContext();

  const { copy } = useCopyToClipboard();

  const { enqueueSnackbar } = useSnackbar();

  const { translate } = useLocales();
  const { confirm } = useConfirmationModal();

  const { showShareDriveContent } = useShareDriveContentDialog();
  const { showEditContent } = useDriveContentEditDialog();
  const { deleteContent } = useDeleteContent();

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'filefolderCardMenu"',
  });

  const popupSubMenuState = usePopupState({
    variant: 'popover',
    popupId: 'filefolderSubMenu"',
  });

  const { driveContentToken } = useDriveContentPublicTokenQuery(driveContent);

  const handleCopyUrl = () => {
    if (!driveContent.isPublic || !driveContentToken) return;
    copy(getPublicUrl(driveContentToken?.publicToken));
    enqueueSnackbar(translate('shared.snackbar.link_in_your_clipboard'));
  };

  const handleMenuItemClick = (action: () => void, popupState: any) => {
    action();
    popupState.close();
  };

  const handleDelete = async () => {
    if (
      !(await confirm({
        title: translate('shared.confirmation.are_you_sure_title'),
        message: translate('shared.confirmation.move_content_to_trash', {
          contentName: driveContent.name,
        }),
      }))
    )
      return;
    deleteContent(driveContent._id);
  };

  return (
    <>
      <IconButton {...bindTrigger(popupState)}>
        <Iconify icon="eva:more-vertical-fill" />
      </IconButton>
      <Popover
        {...bindPopover(popupState)}
        onClose={() => popupState.close()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {driveContent.type === DriveContentType.FILE && (
          <MenuItem
            onClick={(event) => {
              handleMenuItemClick(
                () => downloadContent(driveContent),
                popupState,
              );
              event.stopPropagation();
            }}
          >
            <ListItemIcon>
              <Iconify icon="tabler:download" />
            </ListItemIcon>
            <ListItemText>{translate('shared.labels.download')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() =>
            handleMenuItemClick(() => showEditContent(driveContent), popupState)
          }
        >
          <ListItemIcon>
            <Iconify icon="tabler:pencil-minus" />
          </ListItemIcon>

          <ListItemText>{translate('shared.buttons.rename')}</ListItemText>
        </MenuItem>

        <>
          <MenuItem
            {...bindHover(popupSubMenuState)}
            disabled={currentUser?._id !== driveContent.user}
          >
            <ListItemIcon>
              <Iconify icon="tabler:user-plus" />
            </ListItemIcon>

            <ListItemText>{translate('shared.buttons.share')}</ListItemText>
            <Iconify icon="tabler:chevron-right" fontSize={8} />
          </MenuItem>
          <HoverPopover
            {...bindPopover(popupSubMenuState)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <MenuItem
              onClick={() => {
                handleMenuItemClick(() => handleCopyUrl(), popupSubMenuState);
              }}
              disabled={!driveContent.isPublic}
            >
              <ListItemIcon>
                <Iconify icon="tabler:link" />
              </ListItemIcon>
              <ListItemText>{translate('shared.labels.link')}</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleMenuItemClick(
                  () =>
                    showShareDriveContent({
                      selectedContentIds: [driveContent._id],
                      parentId: driveContent.parent || '',
                    }),
                  popupState,
                );
              }}
            >
              <ListItemIcon>
                <Iconify icon="tabler:user-plus" />
              </ListItemIcon>
              <ListItemText>{translate('shared.buttons.share')}</ListItemText>
            </MenuItem>
          </HoverPopover>
        </>

        <MenuItem
          onClick={(event) => {
            handleMenuItemClick(() => handleDelete(), popupState);
            event.stopPropagation();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Iconify icon="eva:trash-2-outline" />
          </ListItemIcon>
          <ListItemText>{translate('shared.buttons.delete')}</ListItemText>
        </MenuItem>
      </Popover>
    </>
  );
};
